import { Fragment } from 'react';
import { G, Path } from '@react-pdf/renderer';
import { constants as tableConstants } from 'components/elements/Table';
import { furnitureIcons } from 'globalConstants/Icons/Report';
import { mapValues, clamp, round, isFunction, difference } from 'lodash';

/**
* @function Furniture
* @description Renders tables and chairs in PDF floor plans with support for 
* different table shapes, chair arrangements, and transformations.
* 
* @param {Object} props
* @param {string} props.areaId - Area ID containing furniture
* @param {Object} props.bbox - Bounding box dimensions
* @param {number} props.scale - Scale factor for dimensions
* @param {Object} props.content - Room configuration data
* @param {Object} props.content.areas - Areas data by ID
* @param {Object} props.content.areas[].table - Table configuration
* @param {string} props.content.areas[].table.shape - Table shape type
* @param {Object} props.content.areas[].table.size - Table dimensions
* @param {Object} props.content.areas[].table.transform - Table position/rotation
* @param {Array<string>} props.content.areas[].table.ghostChairs - IDs of hidden chairs
* 
* @description
* Renders furniture by:
* 1. Calculating table path based on shape type:
*    - Handles notched, bowed, and tapered edges
*    - Supports corner radiuses and slopes
*    - Adjusts for table legs when needed
* 2. Positioning table in room:
*    - Centers table in bounding box
*    - Applies translation and rotation
* 3. Adding chairs around table:
*    - Places chairs based on table shape
*    - Handles chair visibility (ghosting)
*    - Clips chairs outside bounds
* 
* @example
* <Furniture 
*   areaId="room1"
*   scale={0.5}
*   bbox={{width: 600, height: 400}}
*   content={{
*     areas: {
*       room1: {
*         table: {
*           shape: "rectangular",
*           size: {length: 10, width: 5},
*           transform: {x: 0, y: 0, a: 0},
*           ghostChairs: ["chair1", "chair2"]
*         }
*       }
*     }
*   }}
* />
* 
* @note
* - Uses @react-pdf/renderer for PDF output
* - Table paths generated based on shape constants
* - Supports complex table shapes with curves/notches
* - Chairs placed according to table configuration
* - Handles furniture transformations and clipping
* - Chair size scaled relative to table dimensions
*/
const Furniture = (props) => {
  const { areaId, bbox, scale, content: { areas } } = props;
  const { table: { shape, size, transform, ghostChairs } } = areas[areaId];
  const tableSize = mapValues(size, (v) => v * scale);

  // Table Center
  const cx = round(tableSize.length * 2) / 4;
  const cy = round(tableSize.width * 2) / 4;

  // Table transormation
  const tx = (bbox.width / 2) + transform.x * scale;
  const ty = (bbox.height / 2) + transform.y * scale;

  // Define the table path
  const { notched = false, maxSlope = null, pathProps = () => ({}), chairs = () => [] } = tableConstants.tables[shape] ?? {};
  const { corner = null, leftCorner = null, rightCorner = null, length = tableSize.length, bow = null, width = tableSize.width } = pathProps(tableSize);
  const slope = clamp((tableSize.width / 2 - 0.4572 * scale) / length, 0, maxSlope);
  const legWidth = 0.6096 * scale;
  const legLength = length - legWidth;
  const x0 = cx - length / 2;
  const x1 = slope ? (width / 2 - legWidth + corner + slope * x0) / slope : x0;
  const innerTapper = slope * Math.min(x1 - x0, legLength);
  const innerCorner = maxSlope && (x1 - x0 <= legLength) ? 0 : clamp(width - legWidth * 2 + corner * 2, 0, corner) / 2;
  const inner = (notched && width >= (legWidth - corner) * 2 && innerTapper >= 0) ? [
    `v${corner * 2 - legWidth}`,
    `a${corner},${corner} 0 0 1 ${corner},-${corner}`,
    `l${Math.min(legLength + innerCorner, maxSlope ? x1 - x0 : length)} ${-innerTapper}`,
    `a${innerCorner},${innerCorner} 0 0 0 ${innerCorner},-${innerCorner}`,
    `v${maxSlope && (x1 - x0 <= legLength) ? 0 : (legWidth - corner + innerCorner + innerTapper) * 2 - width}`,
    `a${innerCorner},${innerCorner} 0 0 0 -${innerCorner},-${innerCorner}`,
    `l${-Math.min(legLength + innerCorner, maxSlope ? x1 - x0 : length)} ${-innerTapper}`,
    `a${corner},${corner} 0 0 1 -${corner},-${corner}`,
    `v${corner * 2 - legWidth}`] :
    [`v${- width}`];

  const tapper = maxSlope ? slope * length : 0;
  const rightWidth = maxSlope ? width - tapper * 2 : rightCorner ? 0 : null;
  const originX = cx - (length / 2) - rightCorner / 2 + leftCorner / 2;
  const originY = cy - (width / 2) - (leftCorner ?? corner);

  const path = [
    `m ${originX} ${originY}`,
    `q ${length / 2},${bow ? -bow : tapper / 2} ${length},${tapper}`,
    `a${rightCorner ?? corner},${rightCorner ?? corner} 0 0 1 ${rightCorner ?? corner},${rightCorner ?? corner}`,
    `v${rightWidth ?? width}`,
    `a${rightCorner ?? corner},${rightCorner ?? corner} 0 0 1 -${rightCorner ?? corner},${rightCorner ?? corner}`,
    `q${-length / 2},${bow ?? tapper / 2} ${-length},${tapper}`,
    `a${leftCorner ?? corner},${leftCorner ?? corner} 0 0 1 -${leftCorner ?? corner},-${leftCorner ?? corner}`,
    `${inner.join('')}`,
    `a${leftCorner ?? corner},${leftCorner ?? corner} 0 0 1 ${leftCorner ?? corner},-${leftCorner ?? corner}z`,
  ].join('');

  // Add chairs
  const chair = mapValues(tableConstants.chair, (v) => isFunction(v) ? v : v * scale);
  const { chairArray = [] } = chairs(tableSize, chair, slope);
  const Chair = furnitureIcons.chair;

  return (
    <Fragment>
      <G transform={`translate(${round(tx)}, ${round(ty)})`}>
        <G transform={`rotate(${transform.a})`} >
          <Path d={path} transform={`translate(${round(-cx)}, ${round(-cy)})`} stroke='#000' />
          {chairArray.map((ch, i) => {
            if (ghostChairs.includes(ch.id)) return
            const angle = transform.a * Math.PI / 180;
            const xx = ch.x * Math.cos(angle) - ch.y * Math.sin(angle) + tx;
            const yy = ch.x * Math.sin(angle) + ch.y * Math.cos(angle) + ty;
            const rr = chair.size * .51;
            return (xx - rr >= 0 && xx + rr <= bbox.width && yy - rr >= 0 && yy + rr <= bbox.height) && (
              <Chair key={ch.id} arms={true} size={chair.size} {...ch} />
            )
          })}
        </G>
      </G>
    </Fragment>
  )
};

export default Furniture